import { writable, get } from 'svelte/store'

export type AIMode = 'paraphrase' | 'continue' | 'critic' | 'expand' | 'shrink' | 'summarize' | null

export interface SegmentHistoryEntry {
  suggested: string
  reason?: string
  hasChanges: boolean
  status: 'pending' | 'applied' | 'skipped'
  mode?: 'paraphrase' | 'expand' | 'shrink' | 'manual-edit' | 'prompt' | 'continue'
  timestamp: number
  source: 'ai-action' | 'manual-edit' | 'bulk-action'
}

export interface Segment {
  id: string
  original: string
  suggested: string
  reason?: string  // AI가 제안한 변경 이유
  hasChanges: boolean
  selected: boolean
  status: 'pending' | 'applied' | 'skipped'
  mode?: 'paraphrase' | 'expand' | 'shrink' | 'manual-edit' | 'prompt' | 'continue'  // 마짉으로 사용된 AI 도구 또는 사용자 수정
  position: {
    start: number
    end: number
  }
  paragraphBreaks?: number[]  // 합쳐진 문단들의 원본 구분 위치
  history?: SegmentHistoryEntry[]  // 개별 문단의 히스토리
  historyIndex: number  // 현재 히스토리 위치
  isProcessing?: boolean  // 개별 세그먼트 처리 중 상태
  customPrompt?: string  // 프롬프트 모드에서 사용한 프롬프트
  error?: string  // AI 처리 중 발생한 에러 메시지
  retryCallback?: () => Promise<void>  // 재시도를 위한 콜백 함수
}

export interface AIFeaturesState {
  activeMode: AIMode
  isProcessing: boolean
  segments: Segment[]
  splitView: {
    isActive: boolean
    leftContent: string
    rightContent: string
    mode: 'side-by-side' | 'top-bottom' | 'overlay'
    splitRatio: number
  }
  selection: {
    text: string
    start: number
    end: number
  } | null
  error: string | null
}

function createAIFeaturesStore() {
  const { subscribe, set, update } = writable<AIFeaturesState>({
    activeMode: null,
    isProcessing: false,
    segments: [],
    splitView: {
      isActive: false,
      leftContent: '',
      rightContent: '',
      mode: 'side-by-side',
      splitRatio: 50
    },
    selection: null,
    error: null
  })

  return {
    subscribe,
    
    // Set active mode
    setMode(mode: AIMode) {
      update(state => ({ ...state, activeMode: mode }))
    },
    
    // Set processing state
    setProcessing(isProcessing: boolean) {
      update(state => ({ ...state, isProcessing }))
    },
    
    // Set segments
    setSegments(segments: Segment[]) {
      update(state => ({ ...state, segments }))
    },
    
    // Toggle segment selection
    toggleSegment(segmentId: string) {
      update(state => ({
        ...state,
        segments: state.segments.map(s => 
          s.id === segmentId ? { ...s, selected: !s.selected } : s
        )
      }))
    },
    
    // Select all segments
    selectAllSegments() {
      update(state => ({
        ...state,
        segments: state.segments.map(s => ({ ...s, selected: true }))
      }))
    },
    
    // Deselect all segments
    deselectAllSegments() {
      update(state => ({
        ...state,
        segments: state.segments.map(s => ({ ...s, selected: false }))
      }))
    },
    
    // Enter split view
    enterSplitView(leftContent: string, rightContent: string) {
      update(state => ({
        ...state,
        splitView: {
          ...state.splitView,
          isActive: true,
          leftContent,
          rightContent
        }
      }))
    },
    
    // Exit split view
    exitSplitView() {
      update(state => ({
        ...state,
        splitView: {
          ...state.splitView,
          isActive: false,
          leftContent: '',
          rightContent: ''
        },
        activeMode: null,
        segments: []
      }))
    },
    
    // Update split ratio
    setSplitRatio(ratio: number) {
      update(state => ({
        ...state,
        splitView: {
          ...state.splitView,
          splitRatio: Math.max(20, Math.min(80, ratio))
        }
      }))
    },
    
    // Set selection
    setSelection(selection: AIFeaturesState['selection']) {
      update(state => ({ ...state, selection }))
    },
    
    // Set error
    setError(error: string | null) {
      update(state => ({ ...state, error }))
    },
    
    // Save segment state to its history
    saveSegmentHistory(segmentId: string, source: SegmentHistoryEntry['source'] = 'ai-action') {
      update(state => {
        const segments = state.segments.map(segment => {
          if (segment.id === segmentId) {
            const historyEntry: SegmentHistoryEntry = {
              suggested: segment.suggested,
              reason: segment.reason,
              hasChanges: segment.hasChanges,
              status: segment.status,
              mode: segment.mode,
              timestamp: Date.now(),
              source
            }
            
            // Initialize history if it doesn't exist
            const currentHistory = segment.history || []
            
            // Remove any entries after current index (for new branch)
            const newHistory = [
              ...currentHistory.slice(0, segment.historyIndex + 1),
              historyEntry
            ]
            
            return {
              ...segment,
              history: newHistory,
              historyIndex: newHistory.length - 1
            }
          }
          return segment
        })
        return { ...state, segments }
      })
    },
    
    // Undo for specific segment
    undoSegment(segmentId: string) {
      update(state => {
        const segments = state.segments.map(segment => {
          if (segment.id === segmentId && segment.history && segment.historyIndex > 0) {
            const newIndex = segment.historyIndex - 1
            const historyEntry = segment.history[newIndex]
            
            return {
              ...segment,
              suggested: historyEntry.suggested,
              reason: historyEntry.reason,
              hasChanges: historyEntry.suggested !== segment.original,
              status: historyEntry.status,
              mode: historyEntry.mode,
              historyIndex: newIndex
            }
          }
          return segment
        })
        return { ...state, segments }
      })
    },
    
    // Redo for specific segment
    redoSegment(segmentId: string) {
      update(state => {
        const segments = state.segments.map(segment => {
          if (segment.id === segmentId && segment.history && segment.historyIndex < segment.history.length - 1) {
            const newIndex = segment.historyIndex + 1
            const historyEntry = segment.history[newIndex]
            
            return {
              ...segment,
              suggested: historyEntry.suggested,
              reason: historyEntry.reason,
              hasChanges: historyEntry.suggested !== segment.original,
              status: historyEntry.status,
              mode: historyEntry.mode,
              historyIndex: newIndex
            }
          }
          return segment
        })
        return { ...state, segments }
      })
    },
    
    // Check if segment can undo
    canUndoSegment(segmentId: string): boolean {
      const state = get({ subscribe })
      const segment = state.segments.find(s => s.id === segmentId)
      return segment ? !!(segment.history && segment.historyIndex > 0) : false
    },
    
    // Check if segment can redo
    canRedoSegment(segmentId: string): boolean {
      const state = get({ subscribe })
      const segment = state.segments.find(s => s.id === segmentId)
      return segment ? !!(segment.history && segment.historyIndex < segment.history.length - 1) : false
    },
    
    // Update segment with new AI suggestion or manual edit
    updateSegmentSuggestion(segmentId: string, suggested: string, reason?: string, mode?: 'paraphrase' | 'expand' | 'shrink' | 'manual-edit') {
      update(state => {
        const segments = state.segments.map(segment => {
          if (segment.id === segmentId) {
            return {
              ...segment,
              suggested,
              reason,
              mode,
              hasChanges: suggested !== segment.original,
              status: (suggested !== segment.original ? 'pending' : 'skipped') as 'pending' | 'applied' | 'skipped',
              isProcessing: false
            }
          }
          return segment
        })
        return { ...state, segments }
      })
    },
    
    // Set processing state for individual segment
    setSegmentProcessing(segmentId: string, isProcessing: boolean) {
      update(state => {
        const segments = state.segments.map(segment => {
          if (segment.id === segmentId) {
            return { ...segment, isProcessing }
          }
          return segment
        })
        return { ...state, segments }
      })
    },
    
    // Reset store
    reset() {
      set({
        activeMode: null,
        isProcessing: false,
        segments: [],
        splitView: {
          isActive: false,
          leftContent: '',
          rightContent: '',
          mode: 'side-by-side',
          splitRatio: 50
        },
        selection: null,
        error: null
      })
    }
  }
}

export const aiFeaturesStore = createAIFeaturesStore()